/**
 * Phone Number Validation Utilities
 * Supports international phone numbers with common formats.
 *
 * Valid formats:
 *   +1 202-555-0143  |  +44 7700 900123  |  +91 9876543210
 *   (202) 555-0143   |  2025550143       |  +81-90-1234-5678
 *
 * Rules:
 *   - Optional leading "+" for country code (1–3 digits)
 *   - Allowed separators: spaces, dashes (-), dots (.), parentheses ()
 *   - Total digit count (separators excluded) must be 8–15
 *   - Letters and unsupported symbols are rejected
 *
 * Translation:
 *   Pass a `_t(key, fallback)` function as the last argument to get
 *   translated error messages. If omitted, English fallbacks are used.
 */

const identity = (key, fallback) => fallback;

/**
 * Extracts only digits from a phone number string.
 * @param {string} phoneNumber
 * @returns {string}
 */
export const extractDigits = (phoneNumber) => {
  return phoneNumber.replace(/[^0-9]/g, '');
};

/**
 * Validates an international phone number.
 * @param {string} phoneNumber
 * @param {Function} [_t] - optional translation function _t(key, fallback, params?)
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validatePhoneNumber = (phoneNumber, _t = identity) => {
  if (!phoneNumber || !phoneNumber.trim()) {
    return {
      isValid: false,
      error: _t('phone_please_enter', 'Please enter a phone number.'),
    };
  }

  const trimmed = phoneNumber.trim();

  if (/[^0-9+\s\-.()\t]/.test(trimmed)) {
    return {
      isValid: false,
      error: _t(
        'phone_invalid_chars',
        'Invalid phone number. Only digits, +, spaces, dashes, dots, and parentheses are allowed.'
      ),
    };
  }

  const plusCount = (trimmed.match(/\+/g) || []).length;
  if (plusCount > 1 || (plusCount === 1 && trimmed[0] !== '+')) {
    return {
      isValid: false,
      error: _t(
        'phone_plus_start_only',
        'Invalid phone number. The "+" sign must only appear at the start.'
      ),
    };
  }

  const digits = extractDigits(trimmed);

  if (digits.length < 8) {
    return {
      isValid: false,
      error: _t(
        'phone_too_short',
        'Phone number is too short. At least 8 digits are required (currently :count).',
        { count: digits.length }
      ),
    };
  }

  if (digits.length > 15) {
    return {
      isValid: false,
      error: _t(
        'phone_too_long',
        'Phone number is too long. Maximum 15 digits allowed (currently :count).',
        { count: digits.length }
      ),
    };
  }

  return { isValid: true, error: null };
};

/**
 * Sanitizes input for a phone number field.
 * @param {string} phoneNumber
 * @returns {string}
 */
export const sanitizePhoneNumber = (phoneNumber) => {
  let sanitized = phoneNumber.replace(/[^0-9+\s\-.()\t]/g, '');

  if (sanitized.indexOf('+') > 0) {
    sanitized = sanitized.replace(/\+/g, '');
  } else if (sanitized.indexOf('+') === 0) {
    sanitized = '+' + sanitized.slice(1).replace(/\+/g, '');
  }

  return sanitized.slice(0, 20);
};

/**
 * Formats a 10-digit local number for display: (XXX) XXX-XXXX.
 * @param {string} phoneNumber
 * @returns {string}
 */
export const formatPhoneNumber = (phoneNumber) => {
  const digits = extractDigits(phoneNumber);
  if (digits.length !== 10) return phoneNumber;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export default {
  validatePhoneNumber,
  sanitizePhoneNumber,
  extractDigits,
  formatPhoneNumber,
};
